/**
 * Shopper Auth (Emergent-managed Google SSO for public checkout).
 *
 * Provides a minimal context so any public page can ask "who is the current
 * shopper?" and pre-populate forms. On mount we:
 *   1. If the URL fragment contains `session_id=…`, exchange it once via
 *      POST /api/shopper/session, clear the hash, then hydrate.
 *   2. Otherwise call GET /api/shopper/me to re-hydrate an existing cookie.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();
const EMERGENT_AUTH = "https://auth.emergentagent.com/";

const ShopperAuthContext = createContext({
  shopper: null,
  loading: true,
  exchanging: false,
  signIn: () => {},
  signOut: async () => {},
  refresh: async () => {},
  updateProfile: async () => {},
});

export function ShopperAuthProvider({ children }) {
  const [shopper, setShopper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exchanging, setExchanging] = useState(false);
  const exchangedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shopper/me`, { withCredentials: true });
      setShopper(data.shopper || null);
    } catch {
      setShopper(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    // `/property-admin/dashboard` owns its own Google OAuth callback
    // (`PropertyAdminDashboard.handleAuthCallbackThenLoad`, agent/customer
    // accounts) — if we also raced to consume the same `#session_id=...`
    // here, whichever ran first would clear the hash and the other would
    // silently get nothing, sometimes leaving the agent signed in as a
    // shopper instead of landing on their dashboard. Let that page's own
    // handler own the hash exclusively.
    const isAgentOwnedCallback = window.location.pathname === "/property-admin/dashboard";
    if (hash.includes("session_id=") && !exchangedRef.current && !isAgentOwnedCallback) {
      exchangedRef.current = true;
      // Extract session_id from the fragment, exchange it, then clear.
      const sessionId = (hash.match(/session_id=([^&]+)/) || [])[1];
      if (!sessionId) {
        refresh();
        return;
      }
      setExchanging(true);
      // Always clear the fragment (success or failure) so a bad session_id
      // doesn't linger in the address bar and get retried on manual refresh.
      const cleanUrl = window.location.pathname + window.location.search;
      axios
        .post(`${API}/shopper/session`, { session_id: sessionId }, { withCredentials: true })
        .then(({ data }) => setShopper(data.shopper || null))
        .catch(() => setShopper(null))
        .finally(() => {
          window.history.replaceState({}, "", cleanUrl);
          setExchanging(false);
          setLoading(false);
        });
      return;
    }
    refresh();
  }, [refresh]);

  // Kick the shopper off to Emergent's hosted OAuth screen. The redirect
  // target is the shopper's current URL so they return to the same page.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signIn = useCallback((returnTo) => {
    const target = returnTo || (window.location.pathname + window.location.search);
    const redirectUrl = window.location.origin + target;
    window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const signOut = useCallback(async () => {
    try { await axios.post(`${API}/shopper/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
    setShopper(null);
  }, []);

  // Persist phone + billing address to the shopper doc so the next checkout
  // pre-fills them. Silent — caller decides whether to surface errors.
  const updateProfile = useCallback(async ({ name, phone, billing_address }) => {
    try {
      const { data } = await axios.put(
        `${API}/shopper/me`,
        { name: name || "", phone: phone || "", billing_address: billing_address || {} },
        { withCredentials: true },
      );
      setShopper(data.shopper || null);
      return data.shopper;
    } catch {
      return null;
    }
  }, []);

  return (
    <ShopperAuthContext.Provider value={{ shopper, loading, exchanging, signIn, signOut, refresh, updateProfile }}>
      {children}
    </ShopperAuthContext.Provider>
  );
}

export function useShopperAuth() {
  return useContext(ShopperAuthContext);
}
